import { validateProductTargetBids } from '@/utils/spCampaignCreateDraft';
import type {
  SpAddedKeyword,
  SpAddedNegativeKeyword,
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpManualTargetingKind,
} from '@/types/spCampaignCreate';

export type SbThemeType =
  | 'KEYWORDS_RELATED_TO_YOUR_BRAND'
  | 'KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES';

export type SbAddedThemeTarget = {
  themeType: SbThemeType;
  label: string;
  suggestedBid: number | null;
  suggestedBidMin?: number | null;
  suggestedBidMax?: number | null;
  bid: string;
};

export type SbLandingPageKind = 'PRODUCT_LIST' | 'STORE';

export const SB_THEME_TARGETS: ReadonlyArray<{ themeType: SbThemeType; label: string }> = [
  {
    themeType: 'KEYWORDS_RELATED_TO_YOUR_BRAND',
    label: 'Keywords related to your brand',
  },
  {
    themeType: 'KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES',
    label: 'Keywords related to your landing pages',
  },
];

export const SB_COLLECTION_MIN_PRODUCTS = 3;
export const SB_COLLECTION_MAX_PRODUCTS = 10;
export const SB_CREATIVE_FEATURED_ASINS = 3;
export const SB_HEADLINE_MAX = 50;
export const SB_BRAND_NAME_MAX = 30;
export const SB_KEYWORD_BATCH_MAX = 100;

export type SbCollectionProductMode = 'automatic' | 'manual';

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function pad3(value: number): string {
  return String(value).padStart(3, '0');
}

function stamp(clickedAt: Date): string {
  const date = `${pad2(clickedAt.getDate())}/${pad2(clickedAt.getMonth() + 1)}/${clickedAt.getFullYear()}`;
  const time = `${pad2(clickedAt.getHours())}:${pad2(clickedAt.getMinutes())}:${pad2(clickedAt.getSeconds())}.${pad3(clickedAt.getMilliseconds())}`;
  return `${date} ${time}`;
}

export function defaultSbAdGroupName(clickedAt: Date = new Date()): string {
  return `Ad group - ${stamp(clickedAt)}`;
}

export function defaultSbCollectionAdName(clickedAt: Date = new Date()): string {
  return `Collection ad - ${stamp(clickedAt)}`;
}

export function canCreateSbAdGroup(campaign: { adProduct?: string } | null | undefined): boolean {
  return campaign?.adProduct === 'SB';
}

export function collectionAsins(products: SpAdvertisedProduct[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const product of products) {
    const asin = product.asin.trim().toUpperCase();
    if (!asin || seen.has(asin)) continue;
    seen.add(asin);
    out.push(asin);
  }
  return out;
}

export function collectionProductMessage(products: SpAdvertisedProduct[]): string | null {
  if (products.some((product) => !product.asin.trim())) {
    return 'Each product in a collection needs an ASIN.';
  }
  const count = collectionAsins(products).length;
  if (count < SB_COLLECTION_MIN_PRODUCTS) return 'Add at least three products to advertise';
  if (count > SB_COLLECTION_MAX_PRODUCTS) return 'Add at most ten products to feature in your ad.';
  return null;
}

/** A collection features ASINs. Vendor catalogs can reuse one code across many ASINs. */
export function collectionProductKey(product: Pick<SpAdvertisedProduct, 'asin'>): string {
  return product.asin.trim().toUpperCase();
}

export function appendCollectionProducts(
  existing: SpAdvertisedProduct[],
  incoming: SpAdvertisedProduct[],
  max = SB_COLLECTION_MAX_PRODUCTS
): { products: SpAdvertisedProduct[]; trimmed: number } {
  const keys = new Set(existing.map((product) => collectionProductKey(product)));
  const next = [...existing];
  let trimmed = 0;
  for (const product of incoming) {
    const key = collectionProductKey(product);
    if (!key || keys.has(key)) continue;
    if (next.length >= max) {
      trimmed += 1;
      continue;
    }
    keys.add(key);
    next.push(product);
  }
  return { products: next, trimmed };
}

export function moveCollectionProduct(
  products: SpAdvertisedProduct[],
  index: number,
  direction: -1 | 1
): SpAdvertisedProduct[] {
  const target = index + direction;
  if (index < 0 || index >= products.length || target < 0 || target >= products.length) return products;
  const next = [...products];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

export function buildSbCollectionAd(input: {
  adGroupId: string;
  adName: string;
  headline: string;
  brandName: string;
  productMode: SbCollectionProductMode;
  products: SpAdvertisedProduct[];
  brandLogoAssetId?: string;
  landingPageKind?: SbLandingPageKind;
  storePageUrl?: string;
}): Record<string, unknown> {
  const creative: Record<string, unknown> = {
    brandName: input.brandName.trim(),
    headline: input.headline.trim(),
  };
  const brandLogoAssetId = input.brandLogoAssetId?.trim();
  if (brandLogoAssetId) creative.brandLogoAssetID = brandLogoAssetId;
  const ad: Record<string, unknown> = {
    adGroupId: input.adGroupId.toString(),
    name: input.adName.trim(),
    state: 'ENABLED',
    creative,
  };
  // Automatic ads go to /sb/v4/ads/autoCollection (no creative.asins).
  // Manual ads go to productCollectionExtended, which requires exactly 3 featured ASINs.
  if (input.productMode === 'manual') {
    const asins = collectionAsins(input.products);
    creative.asins = asins.slice(0, SB_CREATIVE_FEATURED_ASINS);
    const storeUrl = input.storePageUrl?.trim();
    if (input.landingPageKind === 'STORE' && storeUrl) {
      ad.landingPage = { pageType: 'STORE', url: storeUrl };
    } else {
      ad.landingPage = { pageType: 'PRODUCT_LIST', asins };
    }
  }
  return ad;
}

export function buildSbKeywordPayloads(input: {
  campaignId: string;
  adGroupId: string;
  keywords: SpAddedKeyword[];
}): Array<Record<string, unknown>> {
  return input.keywords.map((row) => ({
    campaignId: input.campaignId.toString(),
    adGroupId: input.adGroupId.toString(),
    keywordText: row.keywordText,
    matchType: row.matchType.toLowerCase(),
    bid: parseFloat(row.bid),
    state: 'enabled',
  }));
}

export function buildSbNegativeKeywordPayloads(input: {
  campaignId: string;
  adGroupId: string;
  keywords: SpAddedNegativeKeyword[];
}): Array<{
  campaign_id: string;
  ad_group_id: string;
  keyword_text: string;
  match_type: 'negativeExact' | 'negativePhrase';
}> {
  return input.keywords.map((row) => ({
    campaign_id: input.campaignId.toString(),
    ad_group_id: input.adGroupId.toString(),
    keyword_text: row.keywordText,
    match_type: row.matchType === 'NEGATIVE_EXACT' ? 'negativeExact' : 'negativePhrase',
  }));
}

export function parseSbKeywordSuggestionTable(data: unknown): Array<{
  keywordText: string;
  matchType: 'BROAD' | 'PHRASE' | 'EXACT';
  suggestedBid: number | null;
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
}> {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const keywords = root.keywords;
  if (!Array.isArray(keywords)) return [];
  const out: Array<{
    keywordText: string;
    matchType: 'BROAD' | 'PHRASE' | 'EXACT';
    suggestedBid: number | null;
    suggestedBidMin: number | null;
    suggestedBidMax: number | null;
  }> = [];
  for (const item of keywords) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const keywordText = typeof row.keyword === 'string' ? row.keyword.trim() : '';
    if (!keywordText || !Array.isArray(row.matches)) continue;
    for (const match of row.matches) {
      if (!match || typeof match !== 'object') continue;
      const matchRow = match as Record<string, unknown>;
      const matchRaw = typeof matchRow.matchType === 'string' ? matchRow.matchType.toLowerCase() : '';
      const matchType =
        matchRaw === 'broad' ? 'BROAD' : matchRaw === 'phrase' ? 'PHRASE' : matchRaw === 'exact' ? 'EXACT' : null;
      if (!matchType) continue;
      const suggestedBid = typeof matchRow.suggestedBid === 'number' ? matchRow.suggestedBid : null;
      const suggestedBidMin = typeof matchRow.bidMin === 'number' ? matchRow.bidMin : null;
      const suggestedBidMax = typeof matchRow.bidMax === 'number' ? matchRow.bidMax : null;
      out.push({ keywordText, matchType, suggestedBid, suggestedBidMin, suggestedBidMax });
    }
  }
  return out;
}

export function parseSbBidRecommendations(data: unknown): Array<{
  keywordText: string;
  matchType: 'BROAD' | 'PHRASE' | 'EXACT';
  suggestedBid: number | null;
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
}> {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const rows = root.keywordsBidsRecommendationSuccessResults;
  if (!Array.isArray(rows)) return [];
  const out: Array<{
    keywordText: string;
    matchType: 'BROAD' | 'PHRASE' | 'EXACT';
    suggestedBid: number | null;
    suggestedBidMin: number | null;
    suggestedBidMax: number | null;
  }> = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const keyword =
      row.keyword && typeof row.keyword === 'object' ? (row.keyword as Record<string, unknown>) : {};
    const keywordText = typeof keyword.keywordText === 'string' ? keyword.keywordText.trim() : '';
    const matchRaw = typeof keyword.matchType === 'string' ? keyword.matchType.toLowerCase() : '';
    const matchType =
      matchRaw === 'broad' ? 'BROAD' : matchRaw === 'phrase' ? 'PHRASE' : matchRaw === 'exact' ? 'EXACT' : null;
    if (!keywordText || !matchType) continue;
    out.push({ keywordText, matchType, ...parseRecommendedBid(row.recommendedBid) });
  }
  return out;
}

function parseRecommendedBid(raw: unknown): {
  suggestedBid: number | null;
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
} {
  const recommended = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const median = recommended.rangeMedian ?? recommended.recommended;
  const start = recommended.rangeStart;
  const end = recommended.rangeEnd;
  return {
    suggestedBid: typeof median === 'number' && Number.isFinite(median) ? median : null,
    suggestedBidMin: typeof start === 'number' && Number.isFinite(start) ? start : null,
    suggestedBidMax: typeof end === 'number' && Number.isFinite(end) ? end : null,
  };
}

export type SbThemeBidRecommendation = {
  themeType: SbThemeType;
  suggestedBid: number | null;
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
};

export function parseSbThemeBidRecommendations(data: unknown): SbThemeBidRecommendation[] {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const rows = root.themesRecommendationSuccessResults;
  if (!Array.isArray(rows)) return [];
  const out: SbThemeBidRecommendation[] = [];
  for (const item of rows) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const themeType = row.themeType;
    if (
      themeType !== 'KEYWORDS_RELATED_TO_YOUR_BRAND' &&
      themeType !== 'KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES'
    ) {
      continue;
    }
    out.push({ themeType, ...parseRecommendedBid(row.recommendedBid) });
  }
  return out;
}

export function applySbThemeBidRecommendations(
  themes: SbAddedThemeTarget[],
  recs: SbThemeBidRecommendation[],
  defaultBid: string
): SbAddedThemeTarget[] {
  if (recs.length === 0) return themes;
  const byType = new Map(recs.map((row) => [row.themeType, row]));
  const fallback = recs.find((row) => row.suggestedBid != null) ?? recs[0];
  return themes.map((row) => {
    if (row.suggestedBid != null) return row;
    const rec = byType.get(row.themeType) ?? fallback;
    if (!rec || rec.suggestedBid == null) return row;
    const bidUnedited = !row.bid.trim() || row.bid.trim() === defaultBid;
    return {
      ...row,
      suggestedBid: rec.suggestedBid,
      suggestedBidMin: rec.suggestedBidMin,
      suggestedBidMax: rec.suggestedBidMax,
      bid: bidUnedited ? rec.suggestedBid.toFixed(2) : row.bid,
    };
  });
}

const SB_TARGET_EXPRESSION: Record<SpAddedProductTarget['expressionType'], string> = {
  ASIN_SAME_AS: 'asinSameAs',
  ASIN_CATEGORY_SAME_AS: 'asinCategorySameAs',
  ASIN_EXPANDED_FROM: 'asinExpandedFrom',
};

export function defaultSbThemeTargets(bid: string): SbAddedThemeTarget[] {
  return SB_THEME_TARGETS.map((row) => ({
    ...row,
    suggestedBid: null,
    suggestedBidMin: null,
    suggestedBidMax: null,
    bid,
  }));
}

export function buildSbThemePayloads(input: {
  campaignId: string;
  adGroupId: string;
  themes: SbAddedThemeTarget[];
}): Array<Record<string, unknown>> {
  return input.themes.map((row) => ({
    campaignId: input.campaignId.toString(),
    adGroupId: input.adGroupId.toString(),
    themeType: row.themeType,
    bid: parseFloat(row.bid),
  }));
}

export function buildSbProductTargetPayloads(input: {
  campaignId: string;
  adGroupId: string;
  targets: SpAddedProductTarget[];
}): Array<Record<string, unknown>> {
  return input.targets.map((row) => ({
    campaignId: input.campaignId.toString(),
    adGroupId: input.adGroupId.toString(),
    bid: parseFloat(row.bid),
    expressionType: SB_TARGET_EXPRESSION[row.expressionType],
    value: row.value.trim(),
  }));
}

export function buildSbNegativeProductPayloads(input: {
  campaignId: string;
  adGroupId: string;
  asins: string[];
  brands?: string[];
}): Array<{ campaign_id: string; ad_group_id: string; asin?: string; brand?: string }> {
  const products = input.asins.map((asin) => ({
    campaign_id: input.campaignId.toString(),
    ad_group_id: input.adGroupId.toString(),
    asin: asin.trim().toUpperCase(),
  }));
  const brands = (input.brands ?? [])
    .map((brand) => brand.trim())
    .filter(Boolean)
    .map((brand) => ({
      campaign_id: input.campaignId.toString(),
      ad_group_id: input.adGroupId.toString(),
      brand,
    }));
  return [...products, ...brands];
}

export function validateSbCollectionAdGroupForm(input: {
  name: string;
  adName: string;
  headline: string;
  brandName: string;
  /** When set, brandName must match one of these brandRegistryName values from GET /brands. */
  registeredBrandNames?: string[];
  /** False while GET /brands is in flight — do not allow free-text nicknames through. */
  brandsReady?: boolean;
  /** True when GET /brands failed (timeout/network); distinct from a real empty registry. */
  brandsLoadFailed?: boolean;
  productMode: SbCollectionProductMode;
  products: SpAdvertisedProduct[];
  targetingKind: SpManualTargetingKind;
  keywords: SpAddedKeyword[];
  themes: SbAddedThemeTarget[];
  productTargets: SpAddedProductTarget[];
  landingPageKind?: SbLandingPageKind;
  storePageUrl?: string;
}): string | null {
  if (!input.name.trim()) return 'Ad group name is required.';
  if (!input.adName.trim()) return 'Ad name is required.';
  const headline = input.headline.trim();
  if (!headline) return 'Headline is required.';
  if (headline.length > SB_HEADLINE_MAX) return `Headline must be ${SB_HEADLINE_MAX} characters or fewer.`;
  const brandName = input.brandName.trim();
  if (!brandName) return 'Brand name is required.';
  if (brandName.length > SB_BRAND_NAME_MAX) {
    return `Brand name must be ${SB_BRAND_NAME_MAX} characters or fewer.`;
  }
  const registered = (input.registeredBrandNames ?? [])
    .map((name) => name.trim())
    .filter(Boolean);
  if (input.brandsReady === false) {
    return 'Still loading Brand Registry names. Wait a moment and try again.';
  }
  // When the brands list failed to load, still allow submit with a typed registry name —
  // the backend re-fetches GET /brands and rejects nicknames. Don't hard-block on timeout.
  if (registered.length === 0) {
    if (input.brandsLoadFailed) {
      if (!brandName) {
        return 'Enter the exact Brand Registry name (for example VGR or IPARAH), then create.';
      }
    } else {
      return 'No Brand Registry brands on this profile. Confirm Brand Registry in Amazon Ads, then refresh.';
    }
  } else if (!registered.includes(brandName)) {
    return `Choose a registered brand (${registered.join(', ')}). Amazon rejects account nicknames.`;
  }
  if (input.productMode === 'manual') {
    const productError = collectionProductMessage(input.products);
    if (productError) return productError.endsWith('.') ? productError : `${productError}.`;
    if (input.landingPageKind === 'STORE' && !input.storePageUrl?.trim()) {
      return 'Choose a Brand Store page.';
    }
  }
  if (input.targetingKind === 'PRODUCT') {
    const targetError = validateProductTargetBids(input.productTargets);
    if (targetError) return targetError.endsWith('.') ? targetError : `${targetError}.`;
    if (input.productTargets.length > SB_KEYWORD_BATCH_MAX) {
      return `Add at most ${SB_KEYWORD_BATCH_MAX} product targets.`;
    }
    return null;
  }
  if (input.keywords.length === 0 && input.themes.length === 0) {
    return 'Add at least one keyword or targeting group.';
  }
  if (input.keywords.length > SB_KEYWORD_BATCH_MAX) {
    return `Add at most ${SB_KEYWORD_BATCH_MAX} keywords.`;
  }
  for (const row of input.keywords) {
    const bid = parseFloat(row.bid);
    if (Number.isNaN(bid) || bid <= 0) return `Enter a bid for “${row.keywordText}”.`;
  }
  for (const row of input.themes) {
    const bid = parseFloat(row.bid);
    if (Number.isNaN(bid) || bid <= 0) return `Enter a bid for “${row.label}”.`;
  }
  return null;
}

export function extractSbAdCreateResults(data: unknown): {
  errors: string[];
  successIds: string[];
} {
  const errors: string[] = [];
  const successIds: string[] = [];
  if (!data || typeof data !== 'object') return { errors, successIds };
  const results = (data as Record<string, unknown>).results;
  if (!Array.isArray(results)) return { errors, successIds };
  for (const item of results) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const status = typeof row.status === 'string' ? row.status.toLowerCase() : '';
    const id = row.adId;
    if (status === 'success' && (typeof id === 'string' || typeof id === 'number')) {
      successIds.push(String(id));
    } else if (status === 'error') {
      const message = typeof row.message === 'string' ? row.message : 'Ad creation failed.';
      errors.push(message);
    }
  }
  return { errors, successIds };
}
